import { buildFyTickFormatter } from './fyTickFormatter'
import { partialFyLabels } from './partialFyLabels'
import type { GranularityUnit } from './dataPipeline'
import type { DynamicsPanel, DynamicsTimeframe } from '~/api/hooks/useDynamicsPanels'

/** T6: resolves the FY-aware x-axis tick formatter for EACH panel of one Dynamics row, or
 *  `undefined` (⇒ every panel keeps TimelineChart's default shortDateTick, byte-identical to
 *  before this existed) whenever the row renders no FY buckets.
 *
 *  Why per PANEL: per-timeframe arms do NOT share a window — each has its own [from, to], and
 *  `isPartialFiscalYear` is a window predicate, so a formatter built against the wrong arm's
 *  window silently drops (or invents) the partial marker.
 *
 *  Sub-period mode is the opposite case: the baseline segment and the in-window sub-periods are
 *  slices of ONE contiguous fetch, so ONE window — the one actually fetched, i.e. from
 *  baselineFromIso when the previous-period panel is on — governs every panel in the row
 *  (final-review I1, mirroring ChartCanvas). Judging the in-window panels against `fromIso` while
 *  the rows were fetched from further back marked a COMPLETE fiscal year as partial. */
export function fyTickFormatterForRow(opts: {
  /** The RAW (requested) display unit. Safe: FY is off the coarsening ladder, so requested
   *  'fy' ⇔ effective 'fy' — the same identity ChartTile's fyLabels derivation relies on. */
  unit: GranularityUnit
  fromIso: string
  toIso: string
  /** The walk's TRUE extended lower bound when the previous-period panel is active — the lower
   *  bound of the fetched window, hence of every panel's partial check in sub-period mode. */
  baselineFromIso?: string | null
  timeframes: DynamicsTimeframe[]
}): ((panel: DynamicsPanel) => ((v: string | number) => string) | undefined) | undefined {
  const { unit, fromIso, toIso, baselineFromIso = null, timeframes } = opts
  if (unit !== 'fy') return undefined
  if (timeframes.length > 0) {
    // Per-TF mode: both families fold at `unit` when that unit is 'fy' (buildPerTimeframePanels;
    // the volume arm's fy fold is final-review I2), so both get FY labels here.
    const byId = new Map(timeframes.map((tf) => [tf.id, tf]))
    return (panel) => {
      const tf = byId.get(panel.key)
      // no-silent-fallbacks: an unidentifiable arm gets NO label rather than one checked against
      // some other arm's window (which would mis-state whether its FY bucket is complete).
      return tf ? buildFyTickFormatter(tf.from, tf.to) : undefined
    }
  }
  const fetched = buildFyTickFormatter(baselineFromIso ?? fromIso, toIso)
  return () => fetched
}

/** T7 (dynamics note gap): the row-level twin of fyTickFormatterForRow — same opts, same window
 *  resolution, but returns the FY labels for the row's PartialFyNote instead of a tick formatter.
 *  Reuses partialFyLabels (ChartTile) — no fiscal arithmetic lives here, only bounds-selection.
 *
 *  MUST resolve to exactly the fiscal years fyTickFormatterForRow(same opts) can ever asterisk for
 *  SOME panel in the row — call it with the SAME opts object the row's tickFormatterFor call uses,
 *  or the note will name fiscal years its own ticks don't mark (and vice versa).
 *
 *  Per-timeframe arms don't share a window (see fyTickFormatterForRow's doc) — each arm's own
 *  [from, to] decides its own asterisk, so this unions every arm's partial labels (deduped, in
 *  arm order) so the note covers whichever arm's tick actually carries one. Sub-period mode has
 *  ONE fetched window covering every panel in the row (baselineFromIso ?? fromIso -> toIso) —
 *  mirrors fyTickFormatterForRow's `fetched` branch exactly. */
export function fyNoteLabelsForRow(opts: {
  unit: GranularityUnit
  fromIso: string
  toIso: string
  baselineFromIso?: string | null
  timeframes: DynamicsTimeframe[]
}): string[] {
  const { unit, fromIso, toIso, baselineFromIso = null, timeframes } = opts
  if (unit !== 'fy') return []
  if (timeframes.length > 0) {
    const labels: string[] = []
    for (const tf of timeframes) {
      for (const label of partialFyLabels(tf.from, tf.to)) {
        if (!labels.includes(label)) labels.push(label)
      }
    }
    return labels
  }
  return partialFyLabels(baselineFromIso ?? fromIso, toIso)
}
